// ─── Wolf V2 Feature Switch ───────────────────────────────────────────────────
export const USE_WOLF_V2 = true;

// ─── Asset Paths ──────────────────────────────────────────────────────────────
export const WOLF_V2_ASSET_FOLDER = "assets/06_player_wolf_v2";
// Mobile fix: the original 12288x512 strip exceeded GL_MAX_TEXTURE_SIZE on the
// Motorola (Android) phone GPU (rendered as a black square). The art is repacked into
// a 6x4 grid of 256px frames (1536x1024) which fits within the 2048 safe limit.
// The original strip is kept on disk untouched as a backup.
export const WOLF_V2_RUN_UP_FILE = "wolf_run_up_24f_grid_1536x1024.png";
export const WOLF_V2_RUN_UP_URL = `/${WOLF_V2_ASSET_FOLDER}/${WOLF_V2_RUN_UP_FILE}`;

// ─── Run-Up Animation ─────────────────────────────────────────────────────────
export const WOLF_RUN_UP_V2_ANIM_KEY = "wolf_run_up_24f_v2";
export const WOLF_RUN_UP_V2_FRAME_COUNT = 24;
// Per-frame slice size of the repacked grid sheet (256x256). The on-screen size
// stays at 174px because WolfAnimationSystem re-applies setDisplaySize whenever the
// active animation changes (these frames are 256px while idle/champion frames are
// 512px, so the scale must be recomputed per sheet — see lastAnimKey there).
export const WOLF_RUN_UP_V2_FRAME_WIDTH = 256;
export const WOLF_RUN_UP_V2_FRAME_HEIGHT = 256;
export const WOLF_RUN_UP_V2_EXPECTED_WIDTH = 1536;
export const WOLF_RUN_UP_V2_EXPECTED_HEIGHT = 1024;
export const WOLF_RUN_UP_V2_FPS = 24;

// ─── V2 Animation State Map ───────────────────────────────────────────────────
// Each entry maps a logical animation state to its V2 animation key and the
// V1 fallback animation key used when the V2 asset is not yet available.
// To add a new V2 animation, add a new entry here and register the spritesheet
// + animation in BootScene without touching WolfAnimationSystem logic.
export type WolfV2AnimState = "run" | "runLeft" | "runRight" | "champion" | "dash" | "injured";

export interface WolfV2AnimEntry {
  /** Phaser animation key for the V2 variant (may not yet exist) */
  v2Key: string;
  /** Phaser animation key for the V1 fallback (always exists) */
  v1Key: string;
  /** Frame count in the V2 spritesheet (used for registration) */
  frameCount: number;
  /** Expected spritesheet width in px (frameCount * frameWidth) */
  expectedWidth: number;
  /** Expected spritesheet height in px (= frameHeight = 512) */
  expectedHeight: number;
  /** Relative URL to the V2 asset file */
  url: string;
  /** Per-frame slice width in px. Falls back to WOLF_ANIMATION_CONFIG when omitted. */
  frameWidth?: number;
  /** Per-frame slice height in px. Falls back to WOLF_ANIMATION_CONFIG when omitted. */
  frameHeight?: number;
}

export const WOLF_V2_ANIM_MAP: Record<WolfV2AnimState, WolfV2AnimEntry> = {
  run: {
    v2Key: WOLF_RUN_UP_V2_ANIM_KEY,
    v1Key: "wolf-run",
    frameCount: WOLF_RUN_UP_V2_FRAME_COUNT,
    expectedWidth: WOLF_RUN_UP_V2_EXPECTED_WIDTH,
    expectedHeight: WOLF_RUN_UP_V2_EXPECTED_HEIGHT,
    url: WOLF_V2_RUN_UP_URL,
    frameWidth: WOLF_RUN_UP_V2_FRAME_WIDTH,
    frameHeight: WOLF_RUN_UP_V2_FRAME_HEIGHT
  },
  // ── Diagonal-Left V2 (asset not yet integrated) ─────────────────────────────
  runLeft: {
    v2Key: "wolf_run_left_v2",
    v1Key: "wolf-left",
    frameCount: 0,           // 0 = not yet registered; system will use V1 fallback
    expectedWidth: 0,
    expectedHeight: 512,
    url: `/${WOLF_V2_ASSET_FOLDER}/wolf_run_left_v2_sheet.png`
  },
  // ── Diagonal-Right V2 (asset not yet integrated) ────────────────────────────
  runRight: {
    v2Key: "wolf_run_right_v2",
    v1Key: "wolf-right",
    frameCount: 0,
    expectedWidth: 0,
    expectedHeight: 512,
    url: `/${WOLF_V2_ASSET_FOLDER}/wolf_run_right_v2_sheet.png`
  },
  // ── Champion V2 (asset not yet integrated) ───────────────────────────────────
  champion: {
    v2Key: "wolf_champion_v2",
    v1Key: "wolf-champion",
    frameCount: 0,
    expectedWidth: 0,
    expectedHeight: 512,
    url: `/${WOLF_V2_ASSET_FOLDER}/wolf_champion_v2_sheet.png`
  },
  // ── Dash V2 (asset not yet integrated) ──────────────────────────────────────
  dash: {
    v2Key: "wolf_dash_v2",
    v1Key: "wolf-run",       // no V1 dash; use run as fallback
    frameCount: 0,
    expectedWidth: 0,
    expectedHeight: 512,
    url: `/${WOLF_V2_ASSET_FOLDER}/wolf_dash_v2_sheet.png`
  },
  // ── Injured V2 (asset not yet integrated) ───────────────────────────────────
  injured: {
    v2Key: "wolf_injured_v2",
    v1Key: "wolf-run",       // no V1 injured anim; use run as fallback
    frameCount: 0,
    expectedWidth: 0,
    expectedHeight: 512,
    url: `/${WOLF_V2_ASSET_FOLDER}/wolf_injured_v2_sheet.png`
  }
};
